import { useState } from 'react'
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { CalendarClock, Loader2, Pencil } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { http } from '@/shared/api/axios'
import {
  useShiftDetail,
  useOpenShiftForClaim,
  useCloseShiftForClaim,
} from '@/entities/shift/model/queries'
import { useSite } from '@/entities/site/model/queries'
import { useClient } from '@/entities/client/model/queries'
import { useShiftScans } from '@/entities/patrol-scan'
import { useAttendanceByShift } from '@/entities/attendance/model/queries'
import { ScanFilters, ScanList } from '@/features/patrol-activity'
import type { ScanFilterValue } from '@/features/patrol-activity'
import {
  ShiftMetaBand,
  GuardsAttendanceTable,
  ShiftGpsSection,
  ShiftIncidents,
  ShiftMemoBook,
  ShiftMemoOverviewCard,
  buildGuardRows,
} from '@/features/shift-detail'
import type { GuardRow } from '@/features/shift-detail'
import { EditShiftModal } from '@/features/shift-calendar/EditShiftModal'
import { seriesDeleteBlockedReason, siteDayKey, useScheduleDraft, useSchedulerSelection } from '@/features/schedule-draft'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { usePermissions } from '@/entities/session/hooks/usePermissions'
import { PERMISSIONS } from '@/shared/config/permissions'

interface ShiftGuardLookupItem {
  guardUserId: number
  fullName: string
  assignmentStatus: string
}

const fetchShiftGuardsLookup = async (
  shiftId: number,
): Promise<ShiftGuardLookupItem[]> => {
  const res = await http.get<ShiftGuardLookupItem[]>(
    `/v1/scheduler/shifts/${shiftId}/guards-lookup`,
  )
  return res.data
}

const useShiftGuardsLookup = (shiftId: number) =>
  useQuery({
    queryKey: ['shift-guards-lookup', shiftId] as const,
    queryFn: () => fetchShiftGuardsLookup(shiftId),
    enabled: !!shiftId,
    staleTime: 60_000,
  })

const searchSchema = z.object({
  tab: z.enum(['attendance', 'incidents', 'memo-book']).optional(),
  guard: z.coerce.number().optional(),
  guardUserId: z.coerce.number().optional(),
  tagId: z.coerce.number().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
})

export const Route = createFileRoute('/shifts/$shiftId/')({
  component: ShiftDetailPage,
  staticData: { authorize: true },
  validateSearch: searchSchema,
})

function ShiftDetailPage() {
  const { shiftId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const router = useRouter()
  const id = Number(shiftId)
  const { hasPermission } = usePermissions()
  const [editOpen, setEditOpen] = useState(false)
  const openForClaimMutation = useOpenShiftForClaim()
  const closeForClaimMutation = useCloseShiftForClaim()
  // No frontend check can enumerate a series without a backend query, so the
  // only complete test is whether the draft and the clipboard are both empty
  // — see `seriesDeleteBlockedReason`'s own doc. The draft persists to
  // localStorage independently of any calendar being open — read
  // unconditionally, not gated on an edit/update permission check, since a
  // stale non-empty draft is exactly what this guard exists to catch.
  const draftOps = useScheduleDraft((state) => state.operations)
  const clipboard = useSchedulerSelection((state) => state.clipboard)

  const shift = useShiftDetail(id)
  const site = useSite(shift.data?.siteId ?? 0)
  const client = useClient(site.data?.clientId ?? 0)
  const guardsLookup = useShiftGuardsLookup(id)
  const attendance = useAttendanceByShift(id)
  const scans = useShiftScans(id, {
    guardUserId: search.guardUserId,
    tagId: search.tagId,
    fromDate: search.fromDate,
    toDate: search.toDate,
  })

  if (shift.isLoading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    )
  }
  if (!shift.data) {
    return (
      <div className="p-8 text-center text-muted-foreground">Shift not found.</div>
    )
  }

  const guardNames = buildGuardNameMap(guardsLookup.data)
  const guardLabel = (gid: number) => guardNames[gid] ?? `Guard #${gid}`

  const rows = buildGuardRows({
    guards: shift.data.guards ?? [],
    guardNames,
    attendance: attendance.data ?? [],
    shiftStartTime: shift.data.startTime,
    now: new Date(),
  })

  const guardChips = (shift.data.guards ?? []).map((g) => ({
    guardUserId: g.guardUserId,
    label: guardLabel(g.guardUserId),
  }))

  const initialSelectedGuard =
    search.guardUserId ?? shift.data.guards?.[0]?.guardUserId ?? null

  const guardOptions = dedupeGuardOptions(rows)
  const canViewMemoBook = hasPermission(PERMISSIONS.MemoBookView)
  const canEditShift = hasPermission(PERMISSIONS.SchedulerShiftsUpdate)
  const isOpenForClaim = shift.data.openForClaim
  const claimTogglePending =
    openForClaimMutation.isPending || closeForClaimMutation.isPending
  const handleClaimToggle = () =>
    isOpenForClaim
      ? closeForClaimMutation.mutate(id)
      : openForClaimMutation.mutate(id)

  const tab = search.tab ?? 'attendance'
  const setTab = (next: string) =>
    navigate({
      search: (s) => ({
        ...s,
        tab: next as typeof search.tab,
      }),
    })

  const updateScanFilter = (v: ScanFilterValue) =>
    navigate({ search: (s) => ({ ...s, ...v }) })

  return (
    <div className="flex flex-col">
      <div className="px-4 pt-4 pb-2">
        <PageHeader
          title={shift.data.title}
          breadcrumbs={[
            { label: 'Shifts', href: '/' },
            { label: shift.data.title },
          ]}
          actions={
            <div className="flex gap-2">
              {hasPermission(PERMISSIONS.SchedulerAttendanceView) && (
                <Button variant="outline" asChild>
                  {/*
                    The shift's own day in the site's zone — see the same link in
                    ShiftDetailModal for why the reader's day will not do.
                  */}
                  <Link
                    to="/scheduler/attendance"
                    search={{
                      shiftId: shift.data.id,
                      date: siteDayKey(shift.data.startTime, shift.data.siteTimeZone),
                    }}
                  >
                    <CalendarClock className="mr-2 h-4 w-4" />
                    Attendance
                  </Link>
                </Button>
              )}
              {canEditShift ? (
                <>
                <Button
                  variant="outline"
                  onClick={handleClaimToggle}
                  disabled={claimTogglePending}
                >
                  {claimTogglePending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isOpenForClaim ? 'Close spots' : 'Publish for self-claim'}
                </Button>
                <Button onClick={() => setEditOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                </>
              ) : null}
            </div>
          }
        />
        {shift.data.seriesId && (
          <Badge variant="outline" className="mt-1 w-fit text-xs">
            Recurring
          </Badge>
        )}
      </div>

      <ShiftMetaBand shift={shift.data} site={site.data} client={client.data} />

      {shift.data.note && (
        <section className="border-y bg-amber-50/60 px-4 py-2 text-sm">
          <span className="mr-2 text-xs uppercase tracking-wide text-muted-foreground">
            Note
          </span>
          {shift.data.note}
        </section>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList variant="line" className="mx-4 mt-3 w-fit justify-start gap-6">
          <TabsTrigger value="attendance" className="flex-none px-0">
            Attendance
          </TabsTrigger>
          <TabsTrigger value="incidents" className="flex-none px-0">
            Incidents
          </TabsTrigger>
          {canViewMemoBook && (
            <TabsTrigger value="memo-book" className="flex-none px-0">
              Memo Book
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="attendance">
          <GuardsAttendanceTable
            rows={rows}
            isLoading={guardsLookup.isLoading}
            siteTimeZone={shift.data.siteTimeZone}
          />

          <ShiftGpsSection
            key={id}
            shiftId={id}
            site={site.data}
            guards={guardChips}
            initialGuardUserId={initialSelectedGuard}
            guardLabel={guardLabel}
          />

          <ShiftMemoOverviewCard shiftId={id} onViewAll={() => setTab('memo-book')} />
        </TabsContent>

        <TabsContent value="incidents">
          {hasPermission(PERMISSIONS.IncidentsReview) && (
            <ShiftIncidents shiftId={id} getGuardName={guardLabel} />
          )}

          <section className="px-4 py-3">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Patrol scans ({scans.data?.totalCount ?? 0})
            </h3>
            <ScanFilters
              guards={guardChips}
              tags={[]}
              value={{
                guardUserId: search.guardUserId,
                tagId: search.tagId,
                fromDate: search.fromDate,
                toDate: search.toDate,
              }}
              onChange={updateScanFilter}
            />
            <ScanList
              scans={scans.data?.items ?? []}
              isLoading={scans.isLoading}
              emptyMessage="No scans recorded for this shift."
              getGuardName={guardLabel}
            />
          </section>
        </TabsContent>

        {canViewMemoBook && (
          <TabsContent value="memo-book">
            <ShiftMemoBook
              shiftId={id}
              guardOptions={guardOptions}
              initialGuard={search.guard}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* A series delete removes every occurrence server-side regardless of
          what's loaded here — and nothing is: no calendar is loaded on this
          page at all. A non-empty draft (it persists across the route change
          that got here) might hold an unsaved change to some other occurrence
          of this shift's series, and a clipboard entry copied before
          navigating here isn't a draft op until it's pasted, which a full
          series delete would destroy unseen either way —
          seriesDeleteBlockedReason disables the checkbox for exactly those
          cases instead of leaving it enabled. */}
      <EditShiftModal
        open={editOpen}
        onOpenChange={setEditOpen}
        shiftId={id}
        seriesDeleteBlockedReason={seriesDeleteBlockedReason(draftOps, clipboard)}
        // This whole page is the shift that was just deleted — staying put
        // would only refetch a resource that's gone. Back rather than a fixed
        // route, since this page is reached from more than one place (the
        // scheduler, the dashboard's Today's Shifts card, a direct link).
        onDeleted={() => router.history.back()}
      />
    </div>
  )
}

function dedupeGuardOptions(
  rows: GuardRow[],
): { id: number; name: string }[] {
  const seen = new Set<number>()
  const out: { id: number; name: string }[] = []
  for (const r of rows) {
    if (seen.has(r.guardUserId)) continue
    seen.add(r.guardUserId)
    out.push({ id: r.guardUserId, name: r.fullName ?? `Guard #${r.guardUserId}` })
  }
  return out
}

function buildGuardNameMap(
  lookup: ShiftGuardLookupItem[] | undefined,
): Record<number, string> {
  const out: Record<number, string> = {}
  for (const g of lookup ?? []) out[g.guardUserId] = g.fullName
  return out
}
